import React, { useState, useEffect } from 'react';
import {
  ChatBubbleBottomCenterTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';
import { CounselingRecord } from '../../learning/models/CounselingRecord';
import { CounselingRecordFormModal } from '../../learning/components/CounselingRecordFormModal';

// ==========================================
// Types
// ==========================================

interface Student {
  id: string;
  name: string;
  member_number: string;
}

type CounselingRecordWithStudentName = CounselingRecord & {
  student_name?: string;
}

// ==========================================
// Component
// ==========================================

const ConsultationRecordsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<CounselingRecordWithStudentName[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<CounselingRecordWithStudentName | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 선택된 학생 (상담 기록 필터용)
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  // 폼 모달 상태
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedStudentForForm, setSelectedStudentForForm] = useState<Student | null>(null);

  // 학생 검색
  const [studentSearch, setStudentSearch] = useState('');

  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const recordsPerPage = 10;

  // ==========================================
  // 데이터 로드
  // ==========================================

  useEffect(() => {
    loadData();
  }, []);

  // 선택된 학생이나 페이지 변경 시 상담 기록 다시 로드
  useEffect(() => {
    if (!loading) {
      loadCounselingRecords();
    }
  }, [selectedStudent, currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('accessToken');

      // 학생 목록 로드
      const studentsRes = await fetch(`${API_BASE_URL}/v1/students/`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      let studentsData: Student[] = [];
      if (studentsRes.ok) {
        studentsData = await studentsRes.json();
        setStudents(Array.isArray(studentsData) ? studentsData : []);
      } else {
        console.error('학생 목록 로드 실패:', studentsRes.status, await studentsRes.text());
      }

      // 초기 상담 기록 로드 (학생 데이터 전달)
      await loadCounselingRecords(studentsData);

    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setStudents([]);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCounselingRecords = async (studentsData?: Student[]) => {
    // studentsData가 없으면 현재 state 사용
    const studentsToUse = studentsData || students;
    try {
      const token = localStorage.getItem('accessToken');
      const offset = (currentPage - 1) * recordsPerPage;

      let url: string;

      // 선택된 학생이 있으면 학생별 API, 없으면 조직 전체 API
      if (selectedStudent) {
        url = `${API_BASE_URL}/v1/counseling-records/student/${selectedStudent.id}?limit=${recordsPerPage}&offset=${offset}`;
      } else {
        // 현재 사용자 정보로 organization_id 가져오기
        const userRes = await fetch(`${API_BASE_URL}/api/v1/supabase-auth/me`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!userRes.ok) {
          console.error('사용자 정보 로드 실패');
          return;
        }

        const userData = await userRes.json();
        const organizationId = userData.organization_id;

        if (!organizationId) {
          console.error('조직 ID가 없습니다');
          return;
        }

        url = `${API_BASE_URL}/v1/counseling-records/organization/${organizationId}?limit=${recordsPerPage}&offset=${offset}`;
      }

      const recordsRes = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (recordsRes.ok) {
        const recordsData = await recordsRes.json();
        const recordsArray = Array.isArray(recordsData) ? recordsData : [];

        // 학생 이름 매핑 (전달받은 studentsToUse 사용)
        const recordsWithNames = recordsArray.map(record => {
          const student = studentsToUse.find(s => s.id === record.student_id);
          return {
            ...record,
            student_name: student ? student.name : '알 수 없음'
          };
        });

        setRecords(recordsWithNames);

        // 전체 개수는 첫 페이지에서만 추정 (백엔드에서 total count를 반환하지 않으므로)
        if (currentPage === 1) {
          setTotalRecords(recordsArray.length < recordsPerPage ? recordsArray.length : recordsArray.length + 1);
        }
      } else {
        console.error('상담 기록 로드 실패:', recordsRes.status, await recordsRes.text());
      }
    } catch (error) {
      console.error('상담 기록 로드 실패:', error);
      setRecords([]);
    }
  };

  // ==========================================
  // 페이지네이션 계산
  // ==========================================

  // 총 페이지 수 계산
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  // ==========================================
  // 이벤트 핸들러
  // ==========================================

  const handleNewRecord = (student: Student) => {
    setSelectedStudentForForm(student);
    setShowFormModal(true);
  };

  const handleFormSuccess = async () => {
    setShowFormModal(false);
    setSelectedStudentForForm(null);
    await loadData();
  };

  const handleViewDetail = (record: CounselingRecordWithStudentName) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const getCounselingTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      regular: '정기 상담',
      emergency: '긴급 상담',
      goal_setting: '목표 설정',
      performance_review: '성과 검토',
      career: '진로 상담',
      other: '기타'
    };
    return labels[type] || type;
  };

  // ==========================================
  // 렌더링
  // ==========================================

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  // 학생 필터링
  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    student.member_number.toLowerCase().includes(studentSearch.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 헤더 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <ChatBubbleBottomCenterTextIcon className="w-9 h-9 text-blue-600" />
          상담 기록 관리
        </h1>
        <p className="mt-2 text-gray-600">
          학생을 선택하여 상담 기록을 작성하세요
        </p>
      </div>

      {/* Step 1: 학생 선택 - 좌우 배치 */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center justify-between mb-6">
          </div>

          {selectedStudent ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">{selectedStudent.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{selectedStudent.name}</div>
                    <div className="text-xs text-gray-500">{selectedStudent.member_number}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleNewRecord(selectedStudent)}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    상담 작성
                  </button>
                  <button
                    onClick={() => { setSelectedStudent(null); setCurrentPage(1); }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    다른 학생 선택
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="학생 이름으로 검색..."
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && !(e.nativeEvent as any).isComposing && studentSearch.trim()) {
                        const token = localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');
                        try {
                          const res = await fetch(
                            `${API_BASE_URL}/v1/owner/members?search=${encodeURIComponent(studentSearch.trim())}&limit=5`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          if (res.ok) {
                            const data = await res.json();
                            const members = data.members || [];
                            if (members.length > 0) {
                              const exact = members.find((m: any) => m.name === studentSearch.trim());
                              const s = exact || members[0];
                              setSelectedStudent(s);
                              setStudentSearch('');
                            }
                          }
                        } catch {}
                      }
                    }}
                    className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                  {studentSearch && (
                    <button onClick={() => setStudentSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" type="button">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                  {studentSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredStudents.length === 0 ? (
                        <div className="px-4 py-3 text-gray-500 text-center text-sm">검색 결과가 없습니다</div>
                      ) : (
                        filteredStudents.map(student => (
                          <button
                            key={student.id}
                            onClick={() => { setSelectedStudent(student); setStudentSearch(''); }}
                            className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 text-sm">{student.name}</div>
                            <div className="text-xs text-gray-500">{student.member_number}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap">총 {students.length}명</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Step 2: 상담 기록 목록 */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          2단계: 작성된 상담 기록 보기
        </h2>
      </div>

      {/* 상담 기록 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {selectedStudent ? (
              <>
                {selectedStudent.name}님의 상담 기록 ({records.length}건)
              </>
            ) : (
              <>
                전체 상담 기록 ({records.length}건)
              </>
            )}
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {records.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              상담 기록이 없습니다.
            </div>
          ) : (
            records.map(record => (
              <div
                key={record.id}
                onClick={() => handleViewDetail(record)}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-gray-900">
                        {record.student_name || '알 수 없음'}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {getCounselingTypeLabel(record.counseling_type)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {record.content?.substring(0, 100)}
                      {record.content && record.content.length > 100 ? '...' : ''}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>📅 {new Date(record.counseling_date).toLocaleDateString('ko-KR')}</span>
                      <span>⏱️ {record.duration_minutes}분</span>
                      {record.topic && (
                        <span>🏷️ {record.topic}</span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-sm font-medium text-gray-900">
                      평균 {(((record.growth_score || 0) + (record.attitude_score || 0) + (record.concentration_score || 0) + (record.goal_achievement_score || 0)) / 4).toFixed(1)}점
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {records.length > 0 && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-center items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              이전
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 border rounded-lg transition-colors ${
                  currentPage === page
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* 상담 기록 작성 모달 */}
      {showFormModal && selectedStudentForForm && (
        <CounselingRecordFormModal
          isOpen={showFormModal}
          onClose={() => {
            setShowFormModal(false);
            setSelectedStudentForForm(null);
          }}
          studentId={selectedStudentForForm.id}
          studentName={selectedStudentForForm.name}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* 상세보기 모달 */}
      {showDetailModal && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">상담 기록 상세</h3>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedRecord(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">학생</label>
                <div className="text-gray-900">{selectedRecord.student_name || '알 수 없음'}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상담 날짜</label>
                  <div className="text-gray-900">
                    {new Date(selectedRecord.counseling_date).toLocaleDateString('ko-KR')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상담 유형</label>
                  <div className="text-gray-900">{getCounselingTypeLabel(selectedRecord.counseling_type)}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">주요 주제</label>
                <div className="text-gray-900">{selectedRecord.topic}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상담 내용</label>
                <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {selectedRecord.content}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">성장도</label>
                  <div className="text-gray-900">{selectedRecord.growth_score || '-'}/5</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">태도</label>
                  <div className="text-gray-900">{selectedRecord.attitude_score || '-'}/5</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">집중력</label>
                  <div className="text-gray-900">{selectedRecord.concentration_score || '-'}/5</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">목표 달성</label>
                  <div className="text-gray-900">{selectedRecord.goal_achievement_score || '-'}/5</div>
                </div>
              </div>
              {selectedRecord.action_plan && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">실행 계획</label>
                  <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                    {selectedRecord.action_plan}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationRecordsPage;
